import type { KAPLAYCtx } from '../types.ts'
import type { RoomOptions } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { useGameUiTextFrame } from '../objects/game-ui-text-frame.ts'
import { getTiledSize, parsePolygon, getFileCacheStr } from '../utils.ts'

export function usePrintRoom(k: KAPLAYCtx) {
  const cacheStr = getFileCacheStr()
  k.loadSprite('printRoom', `sprites/rooms/print-room.png?${cacheStr}`)
  k.loadSprite('printRoomHover', `sprites/rooms/print-room--hover.png?${cacheStr}`)
  const collidersJson = k.loadJSON(
    'printRoomColliders',
    `colliders/print-room.parsed.json?${cacheStr}`,
  )

  const { showTextFrame } = useGameUiTextFrame(k)
  const { getLangString } = useLang(k)

  function setPrintRoom(roomOpts?: RoomOptions) {
    roomOpts = roomOpts || {}

    const roomObj = k.add()

    k.onLoad(() => {
      const { data } = collidersJson

      roomObj.add([
        k.sprite('printRoom', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('background'),
        'print_room_background',
        'background',
      ])

      roomObj.add([
        k.sprite('printRoomHover', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('hover'),
        'print_room_hover',
        'hover',
      ])

      data.objects.forEach((obj: any) => {
        const poly = parsePolygon(k, obj)
        roomObj.add([k.pos(), k.area({ shape: poly }), k.body({ isStatic: true }), 'colliders'])
      })

      roomObj.add([
        k.level(
          [
            '       ',
            '       ',
            '       ',
            '   ?   ',
            '       ',
            '       ',
            '       ',
            '       ',
            '       ',
            '       ',
            '       ',
            '    1  ',
            '       ',
            '       ',
          ],
          {
            tileWidth: getTiledSize(),
            tileHeight: getTiledSize(),
            tiles: {
              '1': () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'print_room_door_exit',
                'door_exit',
                'door',
              ],
              '?': () => [
                k.sprite('overlay', { anim: 'question' }),
                k.area({ isSensor: true }),
                k.layer('background'),
                'print_room_info',
                'sfx_emit_action',
                'info',
              ],
            },
          },
        ),
        k.layer('objects'),
        'print_room',
      ])

      // Set player position
      k.get('player')[0]?.setPos(k.vec2(getTiledSize(4.5), getTiledSize(10)))

      const doorExitCollision = k.onCollide('player', 'print_room_door_exit', () => {
        k.flash(k.BLACK, 0.5)
        roomObj.destroy()
        doorExitCollision.cancel()
        infoCollision.cancel()
        infoCollisionEnd.cancel()
        if (roomOpts.onExit) roomOpts.onExit()
      })

      const infoCollision = k.onCollide('player', 'print_room_info', () => {
        showTextFrame(getLangString('print_room_info'))
      })

      const infoCollisionEnd = k.onCollideEnd('player', 'print_room_info', () => {
        showTextFrame()
      })
    })

    return roomObj
  }

  return {
    setPrintRoom,
  }
}
